import React, { useCallback, useEffect, useState } from 'react';
import { Loader2, RefreshCw, Zap } from 'lucide-react';
import { fetchApi } from '../../api';
import { AI_SUMMARY_ENDPOINT, ConversationSummary, normalizeSummary } from './messageTypes';

/**
 * The "ИИ" tab: the dialog digest and the autopilot switch.
 *
 * The digest used to be one hardcoded paragraph — "Клиент проявляет высокий
 * интерес…" — shown identically for every customer, which is worse than showing
 * nothing. It now comes from the server. If the endpoint is missing, slow or
 * failing, this panel says so; it never invents a summary.
 *
 * The URL lives in `AI_SUMMARY_ENDPOINT` (messageTypes.ts) and is used here and
 * nowhere else, so switching to the final route is a one-line change there.
 */

interface ChatAiTabProps {
  conversationId: number;
  aiEnabled: boolean;
  autopilot: boolean;
  setAutopilot: (v: boolean) => void;
}

type DigestState =
  | { status: 'loading' }
  | { status: 'ready'; summary: ConversationSummary }
  | { status: 'empty' }
  | { status: 'error'; message: string };

export const ChatAiTab: React.FC<ChatAiTabProps> = ({ conversationId, aiEnabled, autopilot, setAutopilot }) => {
  const [state, setState] = useState<DigestState>({ status: 'loading' });
  const [reloadToken, setReloadToken] = useState(0);

  useEffect(() => {
    let active = true;
    setState({ status: 'loading' });
    fetchApi(AI_SUMMARY_ENDPOINT(conversationId))
      .then(payload => {
        if (!active) return;
        const summary = normalizeSummary(payload);
        setState(summary ? { status: 'ready', summary } : { status: 'empty' });
      })
      .catch((error: Error) => {
        if (!active) return;
        setState({ status: 'error', message: error?.message || 'Не удалось получить выжимку диалога' });
      });
    return () => { active = false; };
  }, [conversationId, reloadToken]);

  const reload = useCallback(() => setReloadToken(token => token + 1), []);
  const handleAutopilot = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => setAutopilot(event.target.checked),
    [setAutopilot],
  );

  return (
    <div role="tabpanel" id="client-panel-ai" aria-labelledby="client-tab-ai">
      <div className="side-card digest-card">
        <div className="side-title" style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
          <Zap size={14} /><span>Выжимка диалога</span>
          {state.status === 'ready' && (
            <span className="digest-source" title={state.summary.source === 'llm' ? 'Текст написан языковой моделью' : 'Собрано по фактам из диалога'}>
              {state.summary.source === 'llm' ? 'модель' : 'по фактам'}
            </span>
          )}
        </div>

        {state.status === 'loading' && (
          <div className="digest-state" role="status">
            <Loader2 size={14} className="spin" aria-hidden="true" /> Собираем выжимку…
          </div>
        )}

        {state.status === 'ready' && (
          <>
            {state.summary.headline && <div className="digest-headline">{state.summary.headline}</div>}
            {state.summary.bullets.length > 0 && (
              <ul className="digest-bullets">
                {state.summary.bullets.map((bullet, index) => <li key={index}>{bullet}</li>)}
              </ul>
            )}
            {state.summary.disclaimer && <div className="digest-disclaimer">{state.summary.disclaimer}</div>}
          </>
        )}

        {state.status === 'empty' && (
          <div className="digest-state">
            Пока нечего обобщать: в диалоге слишком мало сообщений.
          </div>
        )}

        {state.status === 'error' && (
          <div className="digest-error">
            Выжимка недоступна: {state.message}
          </div>
        )}

        <div className="digest-actions">
          <button type="button" className="digest-refresh" onClick={reload} disabled={state.status === 'loading'}>
            <RefreshCw size={11} aria-hidden="true" /> Обновить
          </button>
        </div>
      </div>

      <div className="side-card">
        <div className="side-title">Режим ИИ-Автопилота</div>
        <div className="ai-status-row">
          <span className={`ai-dot ${autopilot && aiEnabled ? 'on' : ''}`}></span>
          {autopilot && aiEnabled ? 'Отвечает автоматически' : 'Ожидает менеджера'}
        </div>
        <div className="ap-mini" style={{ marginTop: '10px' }}>
          <span>Автопилот (глобально)</span>
          <label className="switch">
            <input type="checkbox" className="ap-switch" checked={autopilot} onChange={handleAutopilot} />
            <span className="slider"></span>
          </label>
        </div>
      </div>
    </div>
  );
};
